import {FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {find, filter, head, isEmpty, keys, map, pick, size, flatten} from 'lodash';
import {FormFragmentSchema, ResourceModal, generatePropertyFromSchema} from 'apstra-ui-common';

import StageSearchBox, {getStageSearchSchema} from './StageSearchBox';
import StageInput from './StageInput';
import StageWidgetCustomizationInput from './StageWidgetCustomizationInput';
import {sortingToQueryParam, queryParamToSorting, filtersToQueryParam} from '../../queryParamUtils';
import {
  processorCanRaiseAnomalies, getProcessorByStageName, getStageByName,
  getStageRenderingStrategy, castFilterConverter, getStageDataSchema,
  getDefaultAggregationType, getValueColumnNameFromWidget,
} from '../stageUtils';
import checkForPatterns from '../checkForPatterns';
import {
  WIDGET_TYPES, WIDGET_TYPE_STAGE, DEFAULT_WIDGET_TYPE,
  STAGE_DATA_SOURCE, DEFAULT_STAGE_DATA_SOURCE,
  DEFAULT_STAGE_TIME_SERIES_AGGREGATION
} from '../consts';
import IBAContext from '../IBAContext';
import {WidgetForm} from './WidgetForm';
import {ProbeError} from './ProbeDetails/types';
import {WidgetStageSearchBox} from './WidgetStageSearchBox';
import {Probe, Widget, WidgetComponentType} from '../types';
import {useStageOptionsSchema} from '../hooks/useStageOptionsSchema';

const DEFAULT_WIDGET_PAGE_SIZE = 10;
const SPOTLIGHT_MODE_PAGE_SIZE = 1;

export type WidgetModalProps = {
  errors?: ProbeError[],
  fixedStage?: boolean,
  fixedWidgetType?: boolean,
  helpPageId?: string,
  mode?: 'create' | 'update',
  onClose?: () => void,
  onSuccess?: (args: {result: any, createAnother: any}) => void,
  open: boolean,
  probes: Probe[],
  showCreateAnother: boolean,
  trigger?: ReactNode,
  widget?: any,
};

const WidgetModal: FC<WidgetModalProps> = observer(({
  errors,
  fixedStage = false,
  fixedWidgetType = false,
  helpPageId,
  mode = 'create',
  onClose,
  onSuccess,
  open,
  probes,
  showCreateAnother,
  trigger,
  widget,
}) => {
  const {isFreeform, processorDefinitions} = useContext(IBAContext);
  const [widgetType, setWidgetType] = useState<WidgetComponentType>(DEFAULT_WIDGET_TYPE);
  const [properties, setProperties] = useState<any>({});
  const predefinedDashboards = useMemo(
    () => filter(widget?.referencing_dashboards, (d) => d.predefined_dashboard),
    [widget?.referencing_dashboards]
  );

  const visibleColumnsLength = properties?.stage_customization?.visibleColumns?.length ?? null;

  const selectedStageInfo = useMemo(() => {
    const propertiesStage = properties.stage || pick(widget, ['probe_id', 'stage_name']);
    if (widgetType === WIDGET_TYPE_STAGE && propertiesStage) {
      const {probe_id: probeId, stage_name: stageName} = propertiesStage;
      if (probeId && stageName) {
        const probe = find(probes, {id: probeId});
        if (probe) {
          const processor = getProcessorByStageName({probe, stageName});
          const stage = getStageByName({probe, stageName});
          if (stage && processor) return {probe, processor, stage};
        }
      }
    }
    return null;
  }, [probes, properties.stage, widget, widgetType]);

  const stageDataSourceIsTimeSeries = selectedStageInfo ?
    properties.data_source === STAGE_DATA_SOURCE.time_series : false;
  const stageHasPersistedData = selectedStageInfo?.stage.enable_metric_logging ?? false;
  const isSelectedProbeOperational = selectedStageInfo?.probe.state === 'operational';

  const submitAvailable = useMemo(() => {
    if (!properties.label) return false;
    if (widgetType === WIDGET_TYPE_STAGE) {
      return selectedStageInfo !== null && visibleColumnsLength !== 0 && isSelectedProbeOperational;
    }
    return true;
  }, [isSelectedProbeOperational, properties.label, selectedStageInfo, visibleColumnsLength, widgetType]);

  const renderingStrategy = useMemo(() => {
    if (!selectedStageInfo) return null;
    const {probe, stage} = selectedStageInfo;
    return getStageRenderingStrategy({
      probe,
      stage,
      dataSource: properties.data_source ?? widget?.dataSource,
      showContext: properties.show_context ?? widget?.show_context,
    });
  }, [properties.data_source, properties.show_context, selectedStageInfo, widget?.dataSource, widget?.show_context]);

  const stageCustomizationAvailable = !renderingStrategy?.noCustomization;
  const stageMayHaveAnomalies = stageCustomizationAvailable && selectedStageInfo?.processor &&
    processorCanRaiseAnomalies(selectedStageInfo.processor);

  const stageMayHaveSpotlightMode = renderingStrategy ? !renderingStrategy.noSpotlightMode : true;

  const widgetToProperties = useMemo(() => {
    if (!widget) return {};
    const properties = {
      label: widget.label,
      description: widget.description,
    } as any;
    if (widget.type === WIDGET_TYPE_STAGE) {
      properties.stage = {probe_id: widget.probe_id, stage_name: widget.stage_name};
      properties.stage_customization = {
        visibleColumns: isEmpty(widget.visible_columns) ? null : widget.visible_columns,
        sorting: queryParamToSorting(widget.orderby),
      };
      properties.data_source = widget.data_source;
      properties.time_series_duration = widget.time_series_duration;
      properties.aggregation_type = widget.aggregation_type;
      properties.aggregation_period = widget.aggregation_period;
      properties.filter = (renderingStrategy?.filterDeserializer || castFilterConverter)(widget.filter);
      properties.max_items = widget.spotlight_mode ? SPOTLIGHT_MODE_PAGE_SIZE : widget.max_items;
      properties.anomalous_only = widget.anomalous_only;
      properties.show_context = widget.show_context;
      properties.spotlight_mode = widget.spotlight_mode;
      properties.combine_graphs = widget.combine_graphs;
      properties.value_column_name = getValueColumnNameFromWidget({widget, stage: selectedStageInfo?.stage});
    }
    return properties;
  }, [renderingStrategy?.filterDeserializer, selectedStageInfo?.stage, widget]);

  const setProperty = useCallback((name, value) => {
    setProperties((properties) => {
      let clearFilterOnContextTrigger = renderingStrategy?.clearFilterOnContextTrigger;
      const newProperties = {
        ...properties,
        [name]: value,
      };
      clearFilterOnContextTrigger ||= renderingStrategy?.clearFilterOnContextTrigger;
      if (clearFilterOnContextTrigger) {
        if (name === 'show_context' ||
          (name === 'data_source' && value === 'time_series' && newProperties.show_context)) {
          newProperties.filter = '';
        }
      }
      return newProperties;
    });
  }, [renderingStrategy?.clearFilterOnContextTrigger]);

  const patternDescription = useMemo(() => {
    if (!selectedStageInfo) {
      return null;
    }
    const {probe, stage} = selectedStageInfo;
    const dataSource = properties.data_source; // ?? this.props.widget?.dataSource;
    return checkForPatterns({probe, stageName: stage.name, dataSource});
  }, [properties.data_source, selectedStageInfo]);

  const stageHasAlternativeRenderingStrategy = !!(selectedStageInfo && patternDescription) &&
    (!stageDataSourceIsTimeSeries || patternDescription.renderingStrategy?.alwaysUseContext);

  const usePatternWithPersistedData = patternDescription?.shouldFetchPersistedStageData && properties.show_context;

  // was called on change. could be buggy
  useEffect(
    () => {
      if (!selectedStageInfo) return;
      const {probe_id: probeId, stage_name: stageName} = widget ?? {};
      const {stage, probe} = selectedStageInfo;
      let valueColumnName, aggregationType, dataSource, stageCustomization;
      if (stage.name === stageName && probe.id === probeId) {
        ({
          data_source: dataSource,
          value_column_name: valueColumnName,
          aggregation_type: aggregationType,
          stage_customization: stageCustomization
        } = widgetToProperties);
      }
      setProperty(
        'data_source',
        dataSource ?? DEFAULT_STAGE_DATA_SOURCE
      );
      setProperty(
        'value_column_name',
        valueColumnName ?? head(keys(stage.values))
      );
      setProperty(
        'aggregation_type',
        aggregationType ?? getDefaultAggregationType(stage, valueColumnName, patternDescription)
      );
      setProperty(
        'stage_customization',
        stageCustomization ?? {visibleColumns: null, sorting: {}}
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [properties.stage]
  );

  const useCustomSearchBox = properties.show_context &&
      !stageDataSourceIsTimeSeries &&
      !!renderingStrategy?.SearchComponent;

  const stageOptionsSchema = useStageOptionsSchema({
    aggregationType: properties.aggregation_type,
    dataSource: properties.data_source,
    isProbeOperational: isSelectedProbeOperational,
    isSpotlightMode: properties.spotlight_mode,
    patternDescription,
    processor: selectedStageInfo?.processor,
    renderingStrategy,
    showContext: properties.show_context,
    stage: selectedStageInfo?.stage,
    valueColumnName: properties.value_column_name,
  });

  const formSchema = useMemo(() => {
    const schema: FormFragmentSchema = [
      {
        name: 'type',
        required: true,
        disabled: fixedWidgetType || mode === 'update',
        hidden: size(WIDGET_TYPES) <= 1,
        schema: {
          type: 'string',
          title: 'Type',
          default: DEFAULT_WIDGET_TYPE,
          oneOf: map(WIDGET_TYPES, (value, key) => ({const: key, title: value})),
        },
      },
      {
        name: 'label',
        required: true,
        schema: {type: 'string', title: 'Name'}
      },
    ];
    if (widgetType === WIDGET_TYPE_STAGE) {
      schema.push(...[
        {
          name: 'stage',
          required: true,
          disabled: fixedStage,
          as: StageInput,
          schema: {
            type: 'object',
            title: 'Stage',
            default: {probe_id: null, stage_name: null}
          }
        },
        ...flatten(stageOptionsSchema),
        {
          name: 'stage_customization',
          hidden: !isSelectedProbeOperational,
          as: StageWidgetCustomizationInput,
          schema: {
            type: 'object',
            title: 'Stage Output Customization',
            default: {visibleColumns: null, sorting: {}}
          }
        },
        {
          name: 'filter',
          hidden: !isSelectedProbeOperational || !selectedStageInfo,
          as: WidgetStageSearchBox,
          SearchBoxComponent: useCustomSearchBox ? renderingStrategy?.SearchComponent : StageSearchBox,
          schema: {
            type: 'object'
          }
        },
        {
          name: 'spotlight_mode',
          hidden: !isSelectedProbeOperational || !stageMayHaveSpotlightMode,
          schema: {
            type: 'boolean',
            title: 'Spotlight View',
            description: 'Show an extended view of a single stage item.',
          }
        },
        {
          name: 'max_items',
          disabled: properties.spotlight_mode,
          hidden: !isSelectedProbeOperational || !stageCustomizationAvailable,
          schema: {
            type: 'number',
            title: 'Max Items',
            description: 'Max number of stage data items in widget output.',
            minimum: 0,
            maximum: 100,
            default: DEFAULT_WIDGET_PAGE_SIZE,
          }
        },
      ]);
    }
    schema.push(...[
      {
        name: 'description',
        schema: {type: 'string', title: 'Description'}
      },
    ]);
    return schema;
  }, [
    fixedStage, fixedWidgetType, isSelectedProbeOperational, mode, properties.spotlight_mode,
    renderingStrategy?.SearchComponent, selectedStageInfo, stageCustomizationAvailable,
    stageMayHaveSpotlightMode, stageOptionsSchema, useCustomSearchBox, widgetType
  ]);

  const updateWidgetType = useCallback((widgetType) => {
    setWidgetType(widgetType);
    setProperties((properties) => {
      const newProperties = {...properties};
      const propertiesFromWidget = widgetToProperties;
      for (const {name, schema} of formSchema) {
        if (name === 'type') continue;
        if (!(name in newProperties)) {
          newProperties[name] = propertiesFromWidget?.[name] ?? generatePropertyFromSchema(schema);
        }
      }
      return newProperties;
    });
  }, [formSchema, widgetToProperties]);

  const setSpotlightMode = useCallback((value) => {
    setProperties((properties) => ({
      ...properties,
      spotlight_mode: value,
      max_items: value ? SPOTLIGHT_MODE_PAGE_SIZE : DEFAULT_WIDGET_PAGE_SIZE,
    }));
  }, []);

  const setValueColumnName = useCallback((value) => {
    setProperties((properties) => ({
      ...properties,
      value_column_name: value,
      aggregation_type: selectedStageInfo ?
        getDefaultAggregationType(selectedStageInfo.stage, value, patternDescription) : properties.aggregation_type,
    }));
  }, [patternDescription, selectedStageInfo]);

  const setAggregationType = useCallback((value) => {
    setProperties((properties) => {
      const newProperties = {...properties};
      newProperties.aggregation_type = value;
      if (value === 'none') {
        newProperties.aggregation_period = 0;
      } else if (newProperties.aggregation_period === 0) {
        newProperties.aggregation_period = DEFAULT_STAGE_TIME_SERIES_AGGREGATION;
      }
      return newProperties;
    });
  }, []);

  const resetState = useCallback(() => {
    setProperties({});
    updateWidgetType(widget ? widget.type : DEFAULT_WIDGET_TYPE);
  }, [updateWidgetType, widget]);

  const submit = useCallback(() => {
    const widget: Partial<Widget> = {};
    widget.type = widgetType;
    widget.label = properties.label;
    widget.description = properties.description;
    if (widgetType === WIDGET_TYPE_STAGE) {
      widget.probe_id = properties?.stage?.probe_id ?? null;
      widget.stage_name = properties?.stage?.stage_name ?? null;
      widget.data_source = stageHasPersistedData ? properties.data_source : STAGE_DATA_SOURCE.real_time;
      if (widget.data_source === STAGE_DATA_SOURCE.time_series || usePatternWithPersistedData) {
        widget.time_series_duration = properties.time_series_duration;
        widget.aggregation_period = properties.aggregation_period;
        widget.aggregation_type = properties.aggregation_type;
      }
      let defaultVisibleColumns: string[] | null = null;
      if (widget.data_source === STAGE_DATA_SOURCE.time_series && selectedStageInfo) {
        const {probe, stage, processor} = selectedStageInfo;
        const columns = getStageDataSchema({
          probe,
          stage,
          processor,
          processorDefinitions,
          renderingStrategy: renderingStrategy!,
          valueColumnName: properties.value_column_name,
        });
        defaultVisibleColumns = map(columns, ({name}) => name);
      }
      const visibleColumns = properties?.stage_customization?.visibleColumns ?? [];
      widget.visible_columns = isEmpty(visibleColumns) ? defaultVisibleColumns : visibleColumns;
      widget.orderby = sortingToQueryParam(properties?.stage_customization?.sorting ?? {});
      widget.show_context = stageHasAlternativeRenderingStrategy ? properties.show_context : false;
      widget.filter = (widget.show_context && renderingStrategy?.filterSerializer || filtersToQueryParam)(
        properties.filter,
        selectedStageInfo ? getStageSearchSchema({
          processorDefinitions,
          dataSource: widget.data_source ?? DEFAULT_STAGE_DATA_SOURCE,
          isFreeform: isFreeform,
          ...selectedStageInfo,
        }) : null
      );
      widget.max_items = properties.max_items;
      widget.anomalous_only = stageMayHaveAnomalies ? properties.anomalous_only : false;
      widget.spotlight_mode = stageMayHaveSpotlightMode ? properties.spotlight_mode : false;
      widget.combine_graphs = properties.combine_graphs;
    }
    return {widget};
  }, [
    isFreeform, processorDefinitions, properties.aggregation_period, properties.aggregation_type,
    properties.anomalous_only, properties.combine_graphs, properties.data_source, properties.description,
    properties.filter, properties.label, properties.max_items, properties.show_context, properties.spotlight_mode,
    properties?.stage?.probe_id, properties?.stage?.stage_name, properties?.stage_customization?.sorting,
    properties?.stage_customization?.visibleColumns, properties.time_series_duration, properties.value_column_name,
    renderingStrategy, selectedStageInfo, stageHasAlternativeRenderingStrategy, stageHasPersistedData,
    stageMayHaveAnomalies, stageMayHaveSpotlightMode, usePatternWithPersistedData, widgetType
  ]);

  return (
    <ResourceModal
      helpPageId={helpPageId}
      className='iba-widget-modal'
      mode={mode}
      resourceName='Widget'
      trigger={trigger}
      size='small'
      open={open}
      onClose={onClose}
      resetState={resetState}
      submitAvailable={submitAvailable}
      submit={submit}
      onSuccess={onSuccess}
      showCreateAnother={showCreateAnother}
      notifyOnSuccess={false}
    >
      {/* FIXME: using isEmpty here due to `widget` is undefined on first render which affects WidgetForm's
        WidgetStageSearchBox behavior: when open `Edit Widget` modal for the first time, it's SearchBox is
        empty and won't receive an updated non-empty value due to the way the SearchBox component is implemented
        (this behavior is related to the way filters apply and clean-up when user
        switches between Plain and Basic modes in PlainInputSearchBox) */}
      {({actionInProgress}: any) => !isEmpty(properties) &&
        <WidgetForm
          schema={formSchema}
          widgetType={widgetType}
          setWidgetType={updateWidgetType}
          setSpotlightMode={setSpotlightMode}
          setValueColumnName={setValueColumnName}
          setProperty={setProperty}
          setAggregationType={setAggregationType}
          properties={properties}
          disabled={actionInProgress}
          errors={errors}
          probes={probes}
          selectedStageInfo={selectedStageInfo}
          stageCustomizationAvailable={stageCustomizationAvailable}
          mode={mode}
          predefinedDashboards={predefinedDashboards}
        />
      }
    </ResourceModal>
  );
});

WidgetModal.displayName = 'WidgetModal';

export default WidgetModal;
